import React, { useEffect, useState } from "react";
import { getMerchant } from '../../helpers/ReduxHelpers';
import { apiConstants, getGeneric, postGeneric } from '../../common/constants';
import { useForm } from "react-hook-form";
import { Box, Button, Grid, Typography } from "@mui/material";
import { FormProvider, RHFTextField } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup'
import Loader from "../../common/Loader"
import { useSnackbar } from "notistack";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import ConfirmDialog from "../../common/ConfirmDialog";
import { primaryBtnSx, secondaryBtnSx, filterBarSx } from '../../styles/patterns';
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, SUBTLE } from '../../styles/foundation/colors';
import { primaryTextSx, cellStackSx } from '../orders/list2/cellStyles';



const WalletEmailConfig = () => {
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setLoading] = useState(false);
    const [wdConfig, setWDConfig] = useState(null);
    const [reload, setReload] = useState(0);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleteData, setDeleteData] = useState();
    const [sizePerPage, setSizePerPage] = useState(10);
    const [page, setPage] = useState(0);


    const columns = [
        {
            field: 'emails',
            headerAlign: "left",
            hideSortIcons: true,
            headerName: 'Emails',
            flex: 1.2,
            minWidth: 250,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Box sx={cellStackSx}><Typography sx={primaryTextSx}>{row?.email}</Typography></Box>
                )
            },
        },
        {
            field: 'action',
            headerAlign: "left",
            hideSortIcons: true,
            headerName: 'Action',
            flex: 0.5,
            minWidth: 120,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Button
                        onClick={() => handleCancelOrder(row)}
                        color="error"
                        variant="outlined"
                        size="small"
                    >
                        Delete
                    </Button>

                );
            },
        },
    ];
    const emailListRegExp = /^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,}\s*,\s*)*[\w+-.%]+@[\w-.]+\.[A-Za-z]{2,}$/

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                email: Yup.string()
                    .matches(emailListRegExp, { message: 'Invalid email format', excludeEmptyString: true })
                    .required("Email is required!"),
            })
        ),
        defaultValues: {
            email: '',
        }
    })
    const { handleSubmit: handleSubmitForm, reset } = methods

    const postMerchantConfig = (payload) => {
        postGeneric(`${apiConstants.WALLET_EMAIL_CONFIG}`, payload).then(response => {
            enqueueSnackbar('Wallet email configuration updated successfully', { variant: 'success' })
            reset()
            setReload(reload => reload + 1)
            setLoading(false)
        })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                
                // Handle specific error cases
                if (err.response?.status === 400) {
                    if (err.response?.data?.message?.includes('emails') && err.response?.data?.message?.includes('required')) {
                        enqueueSnackbar('Please add at least one email address', { variant: 'error' });
                    } else {
                        enqueueSnackbar(err.response?.data?.message || 'Invalid request. Please check your configuration.', { variant: 'error' });
                    }
                } else {
                    enqueueSnackbar('Failed to update wallet email configuration. Please try again.', { variant: 'error' });
                }
            })
    }

    const onSubmit = (data) => {
        setLoading(true);
        const existingEmails = wdConfig?.responseObj[0]?.alertEmails ?? [];
        const emailsArray = data.email.split(',').map(email => email.trim());
        const payload = {
            alertEmails: [...existingEmails, ...emailsArray],
            merchantId: merchantId
        }
        postMerchantConfig(payload)
    }

    const handleCancelOrder = (data) => {
        setDeleteData(data)
        setDeleteConfirm(true)
    }
    const handleDelete = () => {
        const index = wdConfig?.responseObj[0]?.alertEmails.indexOf(deleteData.email);
        if (index !== -1) {
            wdConfig?.responseObj[0]?.alertEmails.splice(index, 1);
        }
        const payload = {
            alertEmails: [...wdConfig?.responseObj[0]?.alertEmails],
            merchantId: merchantId
        }
        postMerchantConfig(payload)
    }

    useEffect(() => {
        setLoading(true)
        getGeneric(`${apiConstants.GET_ALL_WALLET}?merchantId=${merchantId}`).then((res) => {
            setWDConfig(res.data);
            setLoading(false)
        }).catch((err) => {
            console.log(err)
            setLoading(false)
        })
    }, [merchantId, reload])
    
    return (
        <>
            {(isLoading) && <Loader />}
            <ConfirmDialog
                title=''
                open={deleteConfirm}
                setOpen={setDeleteConfirm}
                onConfirm={() => handleDelete()}
            >
                Are you sure you want to delete ?
            </ConfirmDialog>
            <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                    <Box sx={filterBarSx}>
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.semibold, color: NAVY }}>Wallet Email Configuration</Typography>
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mt: 0.5 }}>Configure email notifications for wallet alerts and transactions</Typography>
                        </Box>
                        <FormProvider methods={methods} onSubmit={handleSubmitForm(onSubmit)}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <RHFTextField
                                        name="email"
                                        label="Add Emails *"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LoadingButton loading={isLoading}
                                        type="submit"
                                        variant="contained"
                                        size="small"
                                        sx={primaryBtnSx}
                                    >Add</LoadingButton>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        sx={{ ml: 1, ...secondaryBtnSx }}
                                        onClick={() => reset()}
                                    >Reset</Button>
                                </Grid>
                            </Grid>
                        </FormProvider>
                    </Box>
                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <ProzoDataGrid
                            columns={columns}
                            autoHeight={true}
                            rows={wdConfig?.responseObj[0]?.alertEmails?.length > 0
                                ? wdConfig?.responseObj[0]?.alertEmails?.map((email, index) => ({ id: index, email }))
                                : []
                            }
                            sizePerPage={sizePerPage}
                            setSizePerPage={setSizePerPage}
                            setPage={setPage}
                            page={page}
                            paginationMode='client'
                            pagination={true}
                            rowCount={wdConfig?.responseObj[0]?.alertEmails?.length || 0}
                            // hideCountNumber={true}
                            getRowId={(row) => row.id}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default WalletEmailConfig;
